import { StandardTaskTrade } from '@kaiizen/kai-scan-template-system';

/**
 * Normalized trade definitions for consistent usage across contractors, task templates, scans, and overrides.
 *
 * All trade identifiers use lowerCamelCase format for consistency.
 * This ensures that trade references are normalized and can be validated across the system.
 */

export interface TradeDefinition {
  slug: StandardTaskTrade; // lowerCamelCase identifier (e.g., "painting", "electrical")
  label: string; // Human-readable label (e.g., "Painting", "Electrical")
  description?: string; // Optional description of the trade
}

/**
 * Complete list of available trades in the system.
 * All trade slugs must be in lowerCamelCase format.
 */
export const TRADE_DEFINITIONS: TradeDefinition[] = [
  { slug: 'general', label: 'General Contractor' },
  { slug: 'carpenter', label: 'Carpenter' },
  { slug: 'electrician', label: 'Electrician' },
  { slug: 'plumber', label: 'Plumber' },
  { slug: 'hvac', label: 'HVAC' },
  { slug: 'roofer', label: 'Roofer' },
  { slug: 'mason', label: 'Mason' },
  { slug: 'flooring', label: 'Flooring' },
  { slug: 'tiler', label: 'Tiler' },
  { slug: 'painting', label: 'Painting' },
  { slug: 'insulation', label: 'Insulation' },
  { slug: 'glazier', label: 'Glazier' },
  { slug: 'windows', label: 'Windows' },
  { slug: 'countertops', label: 'Countertops' },
  { slug: 'cabinets', label: 'Cabinets' },
  { slug: 'concrete', label: 'Concrete' },
  { slug: 'steel', label: 'Steel' },
  { slug: 'excavation', label: 'Excavation' },
  { slug: 'landscaper', label: 'Landscaper' },
  { slug: 'pestControl', label: 'Pest Control' },
  { slug: 'septic', label: 'Septic' },
  { slug: 'structuralEngineer', label: 'Structural Engineer' },
  { slug: 'cleaner', label: 'Cleaner' },
  { slug: 'garageDoor', label: 'Garage Door' },
  { slug: 'poolVendor', label: 'Pool Vendor' },
  { slug: 'inHouse', label: 'In-House' },
  { slug: 'appliances', label: 'Appliances Contractor' },
  { slug: 'foundation', label: 'Foundation' },
  { slug: 'locksmithRekey', label: 'Locksmith / Rekey' },
  { slug: 'resurfacing', label: 'Resurfacing' },
];

/**
 * Map of trade slugs to their definitions for fast lookup.
 */
export const TRADE_DEFINITIONS_BY_SLUG = new Map<string, TradeDefinition>(
  TRADE_DEFINITIONS.map(trade => [trade.slug, trade]),
);

/**
 * Array of all valid trade slugs.
 */
export const VALID_TRADE_SLUGS = TRADE_DEFINITIONS.map(trade => trade.slug);

/**
 * Validates that a trade slug is in the normalized format and exists in the system.
 *
 * @param tradeSlug - The trade slug to validate
 * @returns True if the trade slug is valid, false otherwise
 */
export const isValidTradeSlug = (tradeSlug: string): boolean => {
  return TRADE_DEFINITIONS_BY_SLUG.has(tradeSlug);
};

/**
 * Validates an array of trade slugs.
 *
 * @param tradeSlugs - Array of trade slugs to validate
 * @returns Array of invalid trade slugs found
 */
export const validateTradeSlugs = (tradeSlugs: string[]): string[] => {
  return tradeSlugs.filter(slug => !isValidTradeSlug(slug));
};

/**
 * Gets the human-readable label for a trade slug.
 *
 * @param tradeSlug - The trade slug to get the label for
 * @returns The human-readable label or undefined if not found
 */
export const getTradeLabel = (tradeSlug: string): string | undefined => {
  return TRADE_DEFINITIONS_BY_SLUG.get(tradeSlug)?.label;
};

/**
 * Gets the full trade definition for a trade slug.
 *
 * @param tradeSlug - The trade slug to get the definition for
 * @returns The trade definition or undefined if not found
 */
export const getTradeDefinition = (tradeSlug: string): TradeDefinition | undefined => {
  return TRADE_DEFINITIONS_BY_SLUG.get(tradeSlug);
};

/**
 * Validates that a contractor has the specified trade.
 *
 * @param contractorTrades - Array of trade slugs the contractor has
 * @param requiredTrade - The trade slug required for the task
 * @returns True if the contractor has the required trade, false otherwise
 */
export const contractorHasTrade = (contractorTrades: string[], requiredTrade: string): boolean => {
  return contractorTrades.includes(requiredTrade);
};

/**
 * Validates that all trades in an array are valid.
 *
 * @param trades - Array of trade slugs to validate
 * @returns True if all trades are valid, false otherwise
 */
export const areValidTrades = (trades: string[]): boolean => {
  return validateTradeSlugs(trades).length === 0;
};

/**
 * Normalizes a trade slug to ensure it's in the correct format.
 * This is useful for converting from legacy enum values or user input.
 *
 * @param tradeSlug - The trade slug to normalize
 * @returns The normalized trade slug or undefined if not found
 */
export const normalizeTradeSlug = (tradeSlug: string): string | undefined => {
  // Handle legacy enum values by converting to lowerCamelCase
  const normalized = tradeSlug.toLowerCase();

  // Check if the normalized value exists in our definitions
  if (isValidTradeSlug(normalized)) {
    return normalized;
  }

  // Try to find a match by converting from PascalCase or other formats
  const possibleMatches = TRADE_DEFINITIONS.filter(trade =>
    trade.slug.toLowerCase() === normalized
    || trade.label.toLowerCase() === normalized
  );

  return possibleMatches[0]?.slug;
};

/**
 * Creates a Record mapping trade slugs to their human-readable labels.
 * This is useful for UI components that need to display trade options.
 *
 * @returns Record<string, string> mapping slug to label
 */
export const getTradeLabelsRecord = (): Record<string, string> => {
  return TRADE_DEFINITIONS.reduce((acc, trade) => {
    acc[trade.slug] = trade.label;
    return acc;
  }, {} as Record<string, string>);
};
